import { useEffect, useMemo, useState } from "react";
import { ImgContent } from "./Questions.style";

export default function QuestionImage({ image, width = 400, height = 400 }) {
  const [dimension, setDimension] = useState({
    width: "100%",
    height: "100%",
    loaded: false,
  });

  useEffect(() => {
    if (!image) return;
    const img = new Image();
    img.src = image;
    img.onload = () => {
      if (img.width > width || img.height > height) {
        dimension.width = "75%";
        dimension.height = "75%";
      }
      dimension.loaded = true;
      setDimension({ ...dimension });
    };
  }, [image]);

  const RenderedImage = useMemo(() => {
    if (!dimension.loaded) return <></>;
    return (
      <ImgContent width={`${width}px`} height={`${height}px`} maxWidth={dimension.width} maxHeight={dimension.height}>
        <img src={image} alt="description-img" />
      </ImgContent>
    );
  }, [dimension]);

  return RenderedImage;
}

export function QuestionMedia({
  media,
  width = 400,
  height = 400,
}) {
  const [dimension, setDimension] = useState({
    width: "100%",
    height: "100%",
    loaded: false,
  });

  const mediaType = useMemo(() => {
    if (!media) return null;

    const url = media.split("?")[0].toLowerCase();

    // Image
    if (
      url.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|avif)$/)
    ) {
      return "image";
    }

    // Video
    if (
      url.match(/\.(mp4|webm|ogg|mov|avi|mkv)$/)
    ) {
      return "video";
    }

    // Audio
    if (
      url.match(/\.(mp3|wav|ogg|aac|flac|m4a)$/)
    ) {
      return "audio";
    }

    return "image";
  }, [media]);

  useEffect(() => {
    if (!media || !mediaType) return;

    // Audio does not need dimension calculation
    if (mediaType === "audio") {
      setDimension({
        width: "100%",
        height: "100%",
        loaded: true,
      });

      return;
    }

    // Image
    if (mediaType === "image") {
      const img = new Image();
      img.src = media;

      img.onload = () => {
        let updatedDimension = {
          width: "100%",
          height: "100%",
          loaded: true,
        };

        if (img.width > width || img.height > height) {
          updatedDimension.width = "75%";
          updatedDimension.height = "75%";
        }

        setDimension(updatedDimension);
      };
    }

    // Video
    if (mediaType === "video") {
      const video = document.createElement("video");
      video.src = media;

      video.onloadedmetadata = () => {
        let updatedDimension = {
          width: "100%",
          height: "100%",
          loaded: true,
        };

        if (video.videoWidth > width || video.videoHeight > height) {
          updatedDimension.width = "75%";
          updatedDimension.height = "75%";
        }

        setDimension(updatedDimension);
      };
    }
  }, [media, mediaType, width, height]);

  const RenderedMedia = useMemo(() => {
    if (!dimension.loaded) return <></>;

    return (
      <ImgContent
        width={`${width}px`}
        height={`${height}px`}
        maxWidth={dimension.width}
        maxHeight={dimension.height}
      >
        {mediaType === "image" && (
          <img src={media} alt="question-media" />
        )}

        {mediaType === "video" && (
          <video controls>
            <source src={media} />
            Your browser does not support the video tag.
          </video>
        )}

        {mediaType === "audio" && (
          <audio controls>
            <source src={media} />
            Your browser does not support the audio element.
          </audio>
        )}
      </ImgContent>
    );
  }, [dimension, media, mediaType, width, height]);

  return RenderedMedia;
}